'use client';

import { useEffect, useState } from 'react';

interface ShowcaseVideo {
  id: string;
  title: string;
  orientation: 'portrait' | 'landscape';
}

const videos: ShowcaseVideo[] = [
  {
    id: 'NExCvcMeFcg',
    title: 'Hayat Interiors — Project Showcase',
    orientation: 'portrait',
  },
  {
    id: 'QnrlSgLXHw8',
    title: 'Hayat Interiors — Project Showcase',
    orientation: 'landscape',
  },
  {
    id: 'wldmqdKIObw',
    title: 'Hayat Interiors — Project Showcase',
    orientation: 'landscape',
  },
  {
    id: 'cslJ9ba7g_Q',
    title: 'Hayat Interiors — Project Showcase',
    orientation: 'landscape',
  },
  {
    id: 'buc1W0X8Aso',
    title: 'Luxury 3BHK Interior Walkthrough at Purva Atmosphere',
    orientation: 'portrait',
  },
  {
    id: '9TSTbZWbQuM',
    title: 'Real Client Testimonial: How Hayat Interiors Transformed Our Home',
    orientation: 'landscape',
  },
  {
    id: 'pWJGHWunxzo',
    title: 'Minimal, Warm, Luxurious — A Home Designed to Feel Timeless',
    orientation: 'portrait',
  },
  {
    id: 'UWGEHDpz4BA',
    title: 'Hayat Interiors — Project Showcase',
    orientation: 'portrait',
  },
];

const FeaturedProjectVideos = () => {
  const [activeVideo, setActiveVideo] = useState<ShowcaseVideo | null>(null);

  useEffect(() => {
    if (!activeVideo) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveVideo(null);
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [activeVideo]);

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-zinc-50">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-20">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2.5 px-4 md:px-5 py-2 md:py-2.5 rounded-full bg-white backdrop-blur-sm border border-black/10 mb-4 md:mb-6">
            <div className="w-1.5 h-1.5 bg-black rounded-full" />
            <span className="text-xs font-medium text-black/90 tracking-widest uppercase">
              In Motion
            </span>
          </div>
          <h2 className="font-headline text-3xl md:text-4xl lg:text-6xl font-normal text-black mb-4 md:mb-6 tracking-tight">
            Watch Our Projects Come to Life
          </h2>
          <p className="text-base md:text-lg text-black/60 max-w-2xl mx-auto font-normal px-4">
            Step inside our recently completed homes
          </p>
        </div>

        {/* Video Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {videos.map((video) => (
            <button
              key={video.id}
              type="button"
              onClick={() => setActiveVideo(video)}
              className="group relative aspect-[4/3] rounded-xl overflow-hidden bg-black shadow-md"
              aria-label={`Play ${video.title}`}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={`https://i.ytimg.com/vi/${video.id}/maxresdefault.jpg`}
                alt={video.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                onError={(e) => {
                  const img = e.currentTarget;
                  if (!img.src.includes('hqdefault')) {
                    img.src = `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`;
                  }
                }}
              />
              <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors duration-200" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-white/95 group-hover:bg-white flex items-center justify-center shadow-lg transition-transform duration-200 group-hover:scale-110">
                  <svg className="w-4 h-4 md:w-6 md:h-6 text-black ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Full-size Video Popup */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4 py-8"
          onClick={() => setActiveVideo(null)}
        >
          <button
            type="button"
            onClick={() => setActiveVideo(null)}
            className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center transition-colors duration-200"
            aria-label="Close video"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div
            className={`relative rounded-2xl overflow-hidden shadow-2xl bg-black ${
              activeVideo.orientation === 'portrait'
                ? 'h-full max-h-[90vh] aspect-[9/16]'
                : 'w-full max-w-4xl aspect-video'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${activeVideo.id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
              title={activeVideo.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default FeaturedProjectVideos;
